import { useEffect, useState } from 'react'
import { Megaphone, Plus, Send, Trash2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { FormField } from '@/components/FormField'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/locale'
import { useAuth } from '@/auth/useAuth'
import {
  useAdminAnnouncements,
  useCreateDraft,
  useDeleteDraft,
  usePublishAnnouncement,
  useUpdateDraft,
} from '@/features/platformAnnouncements/queries'
import type { Announcement, AnnouncementDraftPayload } from '@/features/platformAnnouncements/types'
import { PlatformPage } from './PlatformPage'

function errMsg(err: unknown, fallback = 'Action failed.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

interface EditorState {
  id: number | null
  title: string
  bodyHtml: string
  version: string
  features: string[]
  fixes: string[]
}

const emptyEditor: EditorState = {
  id: null,
  title: '',
  bodyHtml: '',
  version: '',
  features: [],
  fixes: [],
}

export function AnnouncementsPage() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('admin.announcements.manage')
  const listQ = useAdminAnnouncements()

  const [editor, setEditor] = useState<EditorState | null>(null)

  const openNew = () => setEditor({ ...emptyEditor })
  const openEdit = (a: Announcement) => setEditor({
    id: a.id,
    title: a.title,
    bodyHtml: a.bodyHtml,
    version: a.version ?? '',
    features: a.features ?? [],
    fixes: a.fixes ?? [],
  })

  return (
    <PlatformPage
      title="Announcements"
      description="Release notes that fan out to every active customer user (in-app + email)."
      actions={canManage ? (
        <Button onClick={openNew}><Plus className="size-4" /> New announcement</Button>
      ) : null}
    >
      {listQ.isLoading ? (
        <Card><CardContent className="py-10"><LoadingSpinner /></CardContent></Card>
      ) : listQ.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{errMsg(listQ.error)}</AlertDescription>
        </Alert>
      ) : (
        <AnnouncementsList
          rows={listQ.data?.announcements ?? []}
          canManage={canManage}
          onEdit={openEdit}
        />
      )}

      {editor ? (
        <AnnouncementEditorModal
          state={editor}
          onClose={() => setEditor(null)}
          onSaved={(saved) => {
            if (saved) setEditor({
              id: saved.id,
              title: saved.title,
              bodyHtml: saved.bodyHtml,
              version: saved.version ?? '',
              features: saved.features ?? [],
              fixes: saved.fixes ?? [],
            })
            else setEditor(null)
          }}
        />
      ) : null}
    </PlatformPage>
  )
}

function AnnouncementsList({
  rows, canManage, onEdit,
}: {
  rows: Announcement[]
  canManage: boolean
  onEdit: (a: Announcement) => void
}) {
  const deleteM = useDeleteDraft()
  const publishM = usePublishAnnouncement()
  const { toast } = useToast()

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Megaphone}
        title="No announcements yet"
        description={canManage
          ? 'Click "New announcement" to draft your first release note.'
          : 'Release notes will appear here once staff publish them.'}
      />
    )
  }

  const onDelete = async (a: Announcement) => {
    if (!confirm(`Delete draft "${a.title}"? This cannot be undone.`)) return
    try {
      await deleteM.mutateAsync(a.id)
      toast({ title: 'Draft deleted.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not delete', description: errMsg(err) })
    }
  }

  const onPublish = async (a: Announcement) => {
    if (!confirm(`Publish "${a.title}"? This will notify every active customer user via in-app notification and email (per opt-in).`)) return
    try {
      const res = await publishM.mutateAsync(a.id)
      toast({
        title: 'Announcement published',
        description: `Fanned out to ${res.announcement.recipientCount} user${res.announcement.recipientCount === 1 ? '' : 's'}.`,
      })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not publish', description: errMsg(err) })
    }
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <DataTable
          rows={rows}
          columns={[
            { header: 'Title', accessorKey: 'title',
              cell: ({ row }) => (
                <button
                  type="button"
                  className="text-left text-sm font-semibold hover:underline"
                  onClick={() => onEdit(row.original)}
                >
                  {row.original.title}
                </button>
              ),
            },
            { header: 'Version', accessorKey: 'version',
              cell: ({ row }) => (
                <span className="font-mono text-xs text-muted-foreground">
                  {row.original.version || '-'}
                </span>
              ),
            },
            { header: 'Status', accessorKey: 'isDraft',
              cell: ({ row }) => row.original.isDraft
                ? <Badge variant="secondary">draft</Badge>
                : <Badge variant="success">published</Badge>,
            },
            { header: 'Recipients', accessorKey: 'recipientCount',
              cell: ({ row }) => (
                <span className="font-mono text-xs">{row.original.recipientCount}</span>
              ),
            },
            { header: 'Updated', accessorKey: 'updatedAt',
              cell: ({ row }) => (
                <span className="text-xs">{formatDate(row.original.updatedAt)}</span>
              ),
            },
            {
              header: '',
              id: 'actions',
              cell: ({ row }) => (
                <div className="flex items-center justify-end gap-2">
                  {canManage && row.original.isDraft ? (
                    <>
                      <Button
                        size="sm"
                        onClick={() => onPublish(row.original)}
                        disabled={publishM.isPending}
                      >
                        <Send className="size-4" /> Publish
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-7"
                        onClick={() => onDelete(row.original)}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  )
}

function AnnouncementEditorModal({
  state, onClose, onSaved,
}: {
  state: EditorState
  onClose: () => void
  onSaved: (saved: Announcement | null) => void
}) {
  const [title, setTitle] = useState(state.title)
  const [bodyHtml, setBodyHtml] = useState(state.bodyHtml)
  const [version, setVersion] = useState(state.version)
  const [features, setFeatures] = useState<string[]>(state.features.length ? state.features : [''])
  const [fixes, setFixes] = useState<string[]>(state.fixes.length ? state.fixes : [''])
  const createM = useCreateDraft()
  const updateM = useUpdateDraft()
  const { toast } = useToast()

  useEffect(() => {
    setTitle(state.title)
    setBodyHtml(state.bodyHtml)
    setVersion(state.version)
    setFeatures(state.features.length ? state.features : [''])
    setFixes(state.fixes.length ? state.fixes : [''])
  }, [state])

  const onSave = async () => {
    if (!title.trim()) {
      toast({ variant: 'destructive', title: 'Title is required.' })
      return
    }
    const payload: AnnouncementDraftPayload = {
      title: title.trim(),
      bodyHtml,
      version: version.trim() || null,
      features: features.map((f) => f.trim()).filter(Boolean),
      fixes: fixes.map((f) => f.trim()).filter(Boolean),
    }
    try {
      if (state.id == null) {
        const r = await createM.mutateAsync(payload)
        toast({ title: 'Draft saved.' })
        onSaved(r.announcement)
      } else {
        const r = await updateM.mutateAsync({ id: state.id, payload })
        toast({ title: 'Draft updated.' })
        onSaved(r.announcement)
      }
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not save', description: errMsg(err) })
    }
  }

  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title={state.id == null ? 'New announcement' : 'Edit draft'}
      className="max-w-3xl"
    >
      <div className="space-y-3">
        <FormField label="Title" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What's new in Bokapsys" />
        </FormField>
        <FormField label="Version" hint="Optional. Example: 2026.05.12">
          <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="2026.05.12" />
        </FormField>
        <FormField label="Body" hint="HTML allowed. Will render inside the standard email template + in-app modal.">
          <Textarea
            rows={6}
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            placeholder="<p>This release adds...</p>"
          />
        </FormField>
        <StringList label="What's new (features)" items={features} onChange={setFeatures} placeholder="e.g. Per-account financial-report filters" />
        <StringList label="Fixes" items={fixes} onChange={setFixes} placeholder="e.g. Resolved a reconciliation rounding bug" />
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} disabled={createM.isPending || updateM.isPending}>
            Save draft
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function StringList({
  label, items, onChange, placeholder,
}: {
  label: string
  items: string[]
  onChange: (v: string[]) => void
  placeholder?: string
}) {
  const update = (idx: number, val: string) => {
    const next = items.slice()
    next[idx] = val
    onChange(next)
  }
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx))
  const add = () => onChange([...items, ''])
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              value={v}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-7"
              onClick={() => remove(i)}
            >
              <Trash2 className="size-4 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={add} className="mt-2">
        <Plus className="size-4" /> Add row
      </Button>
    </div>
  )
}
